import { useEffect, useState } from "react";
import { Backdrop, CircularProgress } from "@mui/material";
import dayjs from "dayjs";
import ToastMessage from "../../utils/mui/righttoptoast";
import api from "../../api/api";
import { athenaErrorMessage } from "../../api/athenaConfirm";
import {
  CLINICAL_SUB_TYPES,
  clinicalSubTypeLabel,
  defaultAthenaCategory,
  faxRefOf,
  fileFaxInAthena,
  pickOf,
  sameFax,
} from "../athenaFiling";
import { faxDocumentFile } from "../faxApi";
import { classOf, filingsOf, initialGroups } from "../pageGroups";
import PageGroups from "./PageGroups";
import { faxAiAthenaClass, faxAiMatchHint } from "../faxAi";
import { useFaxAiSegments, useFaxAiSuggestedPatient } from "../faxAiParts";
import { greyuploadicon } from "../../landingscreen/patientcharts/tabs/Document/svgs";
import { searchicongrey } from "../../colorcoding/svg/iconsvg";
import { useRef } from "react";
import { useSelector } from "react-redux";
import { getPracticeId } from "../../config/practice";

// Kept for existing imports; the helpers live in ../athenaFiling.js (shared with
// the "Send to Athena" box in Send to EMR).
export { defaultAthenaCategory };

/* ------------------ COMPONENT ------------------ */

/*
 * aiRow: the open fax as the list has it now (with its latest AI read,
 * faxAi.js). When the read matched one chart, that chart is pre-selected
 * ("Suggested from the fax"), and the label picks the class the modal opens
 * on. Both are only a pre-selection: typing in Patient or picking another
 * class replaces them, and nothing is sent until staff press Upload. No
 * read, several charts or none: the patient search starts empty, as before.
 * faxtable.jsx mounts a fresh modal for each open. pickOf / sameFax:
 * ../athenaFiling.js.
 */

/*
 * Page groups (owner 10-13, OSA staff: "classify different pages under
 * different categories ... suggest it when they click 'Send to Athena'"):
 * the fax's fax-v5 read (GET fax/{id}/ai page_segments) opens as one group per
 * document when it found several; "Split pages…" starts from one group of
 * every page. Each group is filed as its own Athena document through the same
 * fileFaxInAthena call as before (one upload per group, started by Upload,
 * never retried by itself); a whole-fax group sends the original file, a
 * part sends GET fax/{id}/document?pages=… (a deterministic cut, so the
 * gateway's once-per-file guard knows a re-file). One work-log entry per
 * filed group. After a partial failure only the groups not filed are sent
 * again, and the patient is locked. One group = today's Send to Athena.
 */
const SendToAthena = ({ open, onClose, selectedFax, aiRow, onFiled, documentUrl = null }) => {
  // The filing location's Athena department (CODE staff file into 3).
  const staffDepartmentId = useSelector((state) => state.userconfig?.config?.departmentid);
  const practiseid = getPracticeId();

  const faxRow = open && selectedFax ? (sameFax(aiRow, selectedFax) ? aiRow : selectedFax) : null;
  const classFromFax = faxAiAthenaClass(faxRow);
  const patientFromFax = useFaxAiSuggestedPatient(faxRow);
  const pickFromFax = pickOf(patientFromFax);
  // Set once staff edit the field / class: the AI read never overrides them.
  const patientTouchedRef = useRef(false);
  const classTouchedRef = useRef(false);

  const [loading, setLoading] = useState(false);
  // Set after a successful upload: the modal stays up for a second with the
  // toast, and Upload must not send the same fax again meanwhile.
  const [sent, setSent] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  const [openToast, setOpenToast] = useState(false);

  const [patientSearch, setPatientSearch] = useState(() => pickFromFax?.patientinfo || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(() => pickFromFax);
  // The id pre-selected from the fax (null once staff pick or type).
  const [prefilledPatientId, setPrefilledPatientId] = useState(() =>
    pickFromFax ? String(pickFromFax.patientid) : null
  );
const searchTimeoutRef = useRef(null);

  const [category, setCategory] = useState(() => classFromFax?.category || defaultAthenaCategory());
  const [clinicalSubType, setClinicalSubType] =
    useState(() => classFromFax?.subclass || "CLINICALDOCUMENT");
  const [internalNote, setInternalNote] = useState("");

  // ---- page groups -----------------------------------------------------
  const [split, setSplit] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const [groupsState, setGroupsState] = useState({ groups: [], excluded: [] });
  const [groupMeta, setGroupMeta] = useState({ cover: new Set(), unreadFrom: null, confidence: null, fromAi: false });
  // group key -> {state: "filing" | "filed" | "error", message}
  const [results, setResults] = useState({});
  const anyFiled = Object.values(results).some((r) => r.state === "filed");

  // The fax's page segments: GET fax/{id}/ai, shared with the viewer's read and
  // asked only when the list says the fax splits (faxAiParts.useFaxAiSegments).
  const segmentsView = useFaxAiSegments(faxRow);
  const listPages = Number(faxRow?.ai?.pages_total) || 0;
  useEffect(() => {
    const total = Number(segmentsView?.pages_total) || listPages;
    if (total) setNumPages((n) => n || total);
  }, [segmentsView, listPages]);

  // Several documents suggested: the modal opens on them (staff can change
  // everything, or go back to one document).
  useEffect(() => {
    if (!segmentsView || split || anyFiled) return;
    const init = initialGroups(numPages || segmentsView.pages_total, segmentsView, classOf(category, clinicalSubType));
    if (init.groups.length > 1) {
      setGroupsState({ groups: init.groups, excluded: [] });
      setGroupMeta({ cover: init.cover, unreadFrom: init.unreadFrom, confidence: init.confidence, fromAi: true });
      setSplit(true);
    } else if (init.fromAi) {
      setGroupMeta({ cover: init.cover, unreadFrom: init.unreadFrom, confidence: init.confidence, fromAi: true });
    }
    // only when the read arrives
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segmentsView]);

  // pdf.js counted the pages (thumbnails): a group still waiting for them gets all
  const onPages = (n) => {
    setNumPages((cur) => cur || n);
    setGroupsState((s) =>
      s.groups.length === 1 && s.groups[0].pages.length === 0
        ? { ...s, groups: [{ ...s.groups[0], pages: Array.from({ length: n }, (_, i) => i + 1) }] }
        : s
    );
  };

  const startSplit = () => {
    const init = initialGroups(numPages, null, classOf(category, clinicalSubType));
    setGroupsState({ groups: init.groups, excluded: [] });
    setGroupMeta((m) => ({ ...m, fromAi: false }));
    setSplit(true);
  };
  const oneDocument = () => {
    setSplit(false);
    setGroupsState({ groups: [], excluded: [] });
  };

  // The chart can arrive after the modal opened (v2 lists ids only; the name
  // comes from GET fax/{id}/ai): until staff touch the field, it follows the
  // fax's suggestion.
  const pickKey = pickFromFax ? `${pickFromFax.patientid}:${pickFromFax.patientinfo}:${pickFromFax.dob}` : "";
  useEffect(() => {
    if (patientTouchedRef.current) return;
    setSelectedPatient(pickFromFax);
    setPatientSearch(pickFromFax ? pickFromFax.patientinfo : "");
    setPrefilledPatientId(pickFromFax ? String(pickFromFax.patientid) : null);
    // pickKey says when the suggestion changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickKey]);

  // Likewise the class, when the label arrives while the modal is open.
  const classKey = classFromFax ? `${classFromFax.category}:${classFromFax.subclass || ""}` : "";
  useEffect(() => {
    if (!classFromFax || classTouchedRef.current) return;
    setCategory(classFromFax.category);
    if (classFromFax.subclass) setClinicalSubType(classFromFax.subclass);
    // classKey says when the class changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classKey]);

  if (!open) return null;

  const patientIsFromFax =
    !!selectedPatient && prefilledPatientId !== null && String(selectedPatient.patientid) === prefilledPatientId;
  const patientHint = patientIsFromFax ? faxAiMatchHint(patientFromFax?.confidence) : "";
  const classHint =
    classFromFax &&
    classFromFax.label &&
    category === classFromFax.category &&
    (category !== "CLINICAL" || clinicalSubType === classFromFax.subclass)
      ? classFromFax.label
      : "";

  /* ------------------ HELPERS ------------------ */

  const handleToastClose = () => setOpenToast(false);

  const resetForm = () => {
    setPatientSearch("");
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedPatient(null);
    setPrefilledPatientId(null);
    patientTouchedRef.current = true;
    classTouchedRef.current = true;
    setCategory(defaultAthenaCategory());
    setClinicalSubType("CLINICALDOCUMENT");
    setInternalNote("");
    setSent(false);
    setSplit(false);
    setGroupsState({ groups: [], excluded: [] });
    setResults({});
  };

  /* ------------------ PATIENT SEARCH ------------------ */

const handlePatientSearch = (value) => {
  patientTouchedRef.current = true;
  setPrefilledPatientId(null);
  setPatientSearch(value);
  setSelectedPatient(null);

  // Clear old timer
  if (searchTimeoutRef.current) {
    clearTimeout(searchTimeoutRef.current);
  }

  if (!value.trim()) {
    setSuggestions([]);
    setShowSuggestions(false);
    return;
  }

  // Debounce API call
  searchTimeoutRef.current = setTimeout(async () => {
    try {
      const res = await api.post(
        `/v1/${practiseid}/autocomplete/dssearch`,
        { searchtxt: value }
      );

      if (Array.isArray(res.data)) {
        setSuggestions(res.data.slice(0, 3));
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error("Search error:", error);
    }
  }, 300); // ✅ 300ms debounce
};


  const handleSuggestionSelect = (item) => {
    patientTouchedRef.current = true;
    setPrefilledPatientId(null);
    setPatientSearch(item.patientinfo || "");
    setSelectedPatient(item);
    setShowSuggestions(false);
  };

  /* ------------------ FAX → FILE ------------------ */

  // The fax PDF from the v2 document route with the staff token (faxApi.js),
  // not the hub's viewdoc and the dallas proxy.
  const fetchFaxPdfAsFile = (faxItem) =>
    faxDocumentFile(faxItem, `Fax_${dayjs().format("YYYYMMDD_HHmmss")}.pdf`);

  /* ------------------ SUBMIT ------------------ */

  // What Upload sends: one whole-fax filing (today), or one per page group.
  const filings = split
    ? filingsOf(groupsState, numPages, internalNote)
    : [{ key: "whole", pages: [], allPages: true, category, subclass: clinicalSubType, internalNote, label: "" }];
  const toFile = filings.filter((f) => results[f.key]?.state !== "filed");

  // Declared before submit, which reads it (TDZ).
  const isUploadDisabled =
    !selectedPatient || !selectedFax || loading || sent || toFile.length === 0 ||
    (split && filings.some((f) => f.pages.length === 0));

  const submit = async () => {
    if (isUploadDisabled) return;
    const many = filings.length > 1;
    let ok = 0;
    let failed = 0;
    let lastError = "";
    try {
      setLoading(true);
      for (const f of toFile) {
        setResults((r) => ({ ...r, [f.key]: { state: "filing" } }));
        try {
          const name = `Fax_${dayjs().format("YYYYMMDD_HHmmss")}${f.allPages ? "" : `_p${f.pages[0]}-${f.pages[f.pages.length - 1]}`}.pdf`;
          // the whole fax: the original file, exactly as before; a group: those pages only
          const file = f.allPages ? await fetchFaxPdfAsFile(selectedFax) : await faxDocumentFile(selectedFax, name, { pages: f.pages });
          // The same FormData (multipart) the Send to Athena box in Send to EMR sends.
          await fileFaxInAthena({
            practiceId: practiseid,
            patientId: selectedPatient.patientid,
            file,
            category: f.category,
            subclass: f.subclass,
            internalNote: f.internalNote,
            faxRef: faxRefOf(selectedFax),
            departmentId: staffDepartmentId,
          });
          ok += 1;
          setResults((r) => ({ ...r, [f.key]: { state: "filed" } }));
          // The fax's work log (faxtable.jsx, owner 10-11): one "Sent to Athena" per document.
          if (typeof onFiled === "function")
            onFiled({
              action: "filed_athena",
              patientId: selectedPatient.patientid,
              athena: true,
              note: many ? f.label : undefined,
              openDone: false,
            });
        } catch (error) {
          console.error(error);
          failed += 1;
          lastError = athenaErrorMessage(error, "Upload failed");
          setResults((r) => ({ ...r, [f.key]: { state: "error", message: lastError } }));
        }
      }
      const allFiled = failed === 0;
      if (allFiled) {
        setSent(true);
        // "Mark done" opens once, after the last document
        if (typeof onFiled === "function" && ok > 0) onFiled({ doneOnly: true, athena: true });
        setToastMessage(many ? `${filings.length} documents filed in Athena` : "Fax document uploaded successfully");
        setToastSeverity("success");
        setOpenToast(true);
        setTimeout(() => {
          resetForm();
          handleCloseModal();
        }, 1000);
      } else {
        // "already in this patient's chart" / "Athena did not answer" read better than a bare failure
        setToastMessage(
          many
            ? `${filings.length - toFile.length + ok} of ${filings.length} documents filed; the others were not. Check them and press File again.`
            : lastError || "Upload failed"
        );
        setToastSeverity("error");
        setOpenToast(true);
      }
    } finally {
      setLoading(false);
    }
  };
const handleCloseModal = () => {
  setOpenToast(false);
  setToastMessage("");
  setToastSeverity("success");
  resetForm();
  onClose();
};


  /* ------------------ UI ------------------ */

  return (
    <>
      <Backdrop open={loading} sx={{ zIndex: 9999, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40">
        <div className="flex min-h-screen items-center justify-center py-4">
          <div className={`w-full ${split ? "max-w-4xl" : "max-w-lg"} mx-3 rounded-xl bg-white shadow-lg`}>

            {/* Header */}
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div className="flex items-center gap-2 text-lg font-semibold">
                {greyuploadicon}
                Send to Athena
              </div>
              <button  onClick={handleCloseModal}>✕</button>
            </div>

            {/* Body */}
            <div className={`px-6 ${split ? "py-3 space-y-3" : "py-4 space-y-4"} text-left`}>

              {/* Patient Search */}
              <div>
                <label className="text-sm font-semibold">
                  Patient
                </label>
                <div className="relative mt-1">
                  <div className="flex items-center gap-2 rounded-md border bg-[#F9FAFB] px-3 py-2">
                    {searchicongrey}
                    <input
                      type="text"
                      value={patientSearch}
                      onChange={(e) =>
                        handlePatientSearch(e.target.value)
                      }
                      // once a document is filed the rest go to the same chart
                      disabled={anyFiled}
                      // a suggested chart is selected whole, so typing replaces it
                      onFocus={(e) => {
                        if (patientIsFromFax) e.target.select();
                      }}
                      placeholder="Search patient"
                      aria-label="Patient"
                      className="flex-1 bg-transparent text-sm outline-none"
                    />
                  </div>

                  {patientIsFromFax && (
                    <div className="mt-1 text-xs text-[#7E7E7E]" data-testid="sta-patient-from-fax">
                      Suggested from the fax — change if wrong
                      {selectedPatient.dob ? ` · DOB ${selectedPatient.dob}` : ""}
                      {patientHint ? ` (${patientHint})` : ""}
                    </div>
                  )}

                  {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow">
                      {suggestions.map((item, i) => (
                        <div
                          key={i}
                          onClick={() =>
                            handleSuggestionSelect(item)
                          }
                          className="cursor-pointer px-3 py-2 text-sm hover:bg-gray-100"
                        >
                          {item.patientinfo}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* One document: Category (+ clinical type), as before; "Split
                  pages…" turns it into page groups (owner 10-13). */}
              {!split && (
                <>
              {/* Category */}
             <div>
    <label className="text-sm font-semibold">Category</label>
  
    <div className="mt-2 flex gap-2">
      {["IMAGING", "CLINICAL"].map((t) => (
        <button
          key={t}
          type="button"
          aria-pressed={category === t}
          onClick={() => {
            classTouchedRef.current = true;
            setCategory(t);
          }}
          className={`rounded-md border px-3 py-1.5 text-xs font-medium transition ${
            category === t
              ? "border-gray-800 bg-gray-100 text-gray-800"
              : "border-gray-300 text-gray-600 hover:bg-gray-50"
          }`}
        >
          {t}
        </button>
      ))}
    </div>
    {classHint && (
      <div className="mt-1 text-xs text-[#7E7E7E]" data-testid="sta-class-from-fax">
        From the AI label: {classHint}
      </div>
    )}
  </div>
  
  
                {/* Clinical subtype */}
                {category === "CLINICAL" && (
                  <div>
                    <label className="text-sm font-semibold">
                      Clinical Document Type
                    </label>
                    <select
                      value={clinicalSubType}
                      onChange={(e) => {
                        classTouchedRef.current = true;
                        setClinicalSubType(e.target.value);
                      }}
                      aria-label="Clinical Document Type"
                      className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                    >
                      {CLINICAL_SUB_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {clinicalSubTypeLabel(t)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                  <button
                    type="button"
                    className="text-xs text-[#2B7FFF] hover:underline"
                    onClick={startSplit}
                    data-testid="sta-split"
                  >
                    {numPages > 1 ? `Split ${numPages} pages into documents…` : "Split pages into documents…"}
                  </button>
                </>
              )}

              {split && (
                <div>
                  <div className="mb-1 flex flex-wrap items-baseline gap-2">
                    <label className="text-sm font-semibold">Documents</label>
                    <span className="text-xs text-[#7E7E7E]" data-testid="sta-groups-hint">
                      {groupMeta.fromAi
                        ? `Suggested from the fax${groupMeta.confidence ? ` (${groupMeta.confidence} confidence)` : ""} — check the pages before filing.`
                        : "Each document is filed in Athena on its own."}
                      {groupMeta.unreadFrom ? ` Pages from ${groupMeta.unreadFrom} on were not read by the AI.` : ""}
                    </span>
                    {!anyFiled && (
                      <button
                        type="button"
                        className="ml-auto text-xs text-[#2B7FFF] hover:underline"
                        onClick={oneDocument}
                        data-testid="sta-one-document"
                      >
                        File as one document
                      </button>
                    )}
                  </div>
                  <PageGroups
                    url={documentUrl}
                    numPages={numPages}
                    onPages={onPages}
                    state={groupsState}
                    setState={setGroupsState}
                    cover={groupMeta.cover}
                    unreadFrom={groupMeta.unreadFrom}
                    results={results}
                    disabled={loading || sent}
                  />
                </div>
              )}

              {/* Internal Note */}
              <div>
                <label className="text-sm font-semibold">
                  Internal Note
                </label>
                <textarea
                  rows={split ? 2 : 3}
                  value={internalNote}
                  onChange={(e) =>
                    setInternalNote(e.target.value)
                  }
                  placeholder="Add internal note (optional)"
                  className="mt-1 w-full rounded-md border px-3 py-2 text-sm resize-none"
                />
              </div>

            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 border-t px-6 py-4">
              <button
           onClick={handleCloseModal}
                className="rounded-md border px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                disabled={isUploadDisabled}
                onClick={submit}
                className={`rounded-md px-4 py-2 text-sm text-white ${
                  isUploadDisabled
                    ? "bg-gray-300"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {split && filings.length > 1
                  ? anyFiled
                    ? `File remaining ${toFile.length}`
                    : `File ${filings.length} documents`
                  : "Upload"}
              </button>
            </div>

          </div>
        </div>
      </div>

      <ToastMessage
        message={toastMessage}
        severity={toastSeverity}
        open={openToast}
        handleClose={handleToastClose}
      />
    </>
  );
};

export default SendToAthena;
